<template>
  <div class="d-inline-block">
    <b-button
      v-ripple.400="'rgba(113, 102, 240, 0.15)'"
      variant="warning"
      @click="open"
    >
      <feather-icon
        icon="MailIcon"
        class="mr-50"
      />
      <span class="align-middle">Mail DSV</span>
    </b-button>

    <b-modal
      v-model="show"
      centered
      size="lg"
      title="Mail client — Souffrance DSV"
      ok-title="Envoyer via Klaviyo"
      ok-variant="primary"
      cancel-title="Annuler"
      :ok-disabled="loading || !canSend"
      :no-close-on-backdrop="true"
      @ok.prevent="send"
    >
      <b-alert
        :show="show && !vars.lien_tracking"
        variant="warning"
        class="p-1"
      >
        <small>Aucun tracking remonté par Shippingbo pour cette commande : la ligne « Suivi du colis » a été retirée.</small>
      </b-alert>

      <b-form-group label="Modèle">
        <v-select
          v-model="templateKey"
          :options="templateOptions"
          :reduce="t => t.value"
          label="label"
          :clearable="false"
        />
      </b-form-group>

      <b-form-group label="Objet">
        <b-form-input
          v-model="subject"
          @input="isDirty = true"
        />
      </b-form-group>

      <b-form-group
        label="Message"
        :description="`Envoyé à ${vars.email || '—'} · **texte** = gras · les lignes commençant par • deviennent une liste`"
      >
        <b-form-textarea
          v-model="body"
          rows="18"
          @input="isDirty = true"
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton, BModal, BFormGroup, BFormInput, BFormTextarea, BAlert,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { mapGetters } from 'vuex'
import ToastificationContentVue from '@/@core/components/toastification/ToastificationContent.vue'

const TEMPLATES = {
  absent: {
    label: 'Modèle 1 — Client absent / injoignable',
    subject: 'Action requise pour la livraison de votre commande {{n_commande}}',
    body: `Bonjour {{prenom}},

Le transporteur DSV a tenté de livrer votre commande {{n_commande}}, mais {{situation}}.

Pour organiser une nouvelle présentation, merci de nous répondre à ce mail en indiquant :
• un numéro de téléphone sur lequel vous êtes joignable,
• vos disponibilités pour la livraison.

Nous vous remercions de nous répondre **avant le {{date_limite}}**. Passé ce délai, la commande risque d'être retournée à notre entrepôt.

Suivi du colis : {{lien_tracking}}

Cordialement,
{{signature}}`,
  },
  adresse: {
    label: 'Modèle 2 — Adresse incomplète / erronée',
    subject: 'Confirmation de votre adresse de livraison pour votre commande {{n_commande}}',
    body: `Bonjour {{prenom}},

Le transporteur DSV n'a pas pu livrer votre commande {{n_commande}}, car l'adresse de livraison semble incomplète ou incorrecte :

{{adresse}}

Pouvez-vous nous confirmer cette adresse ?

Merci de nous répondre **avant le {{date_limite}}**, afin que nous puissions relancer la livraison sans retard. Passé ce délai, la commande risque d'être retournée.

Suivi du colis : {{lien_tracking}}

Cordialement,
{{signature}}`,
  },
}

const DEFAULT_DELAY_DAYS = 5

export default {
  directives: { Ripple },
  components: {
    BButton, BModal, BFormGroup, BFormInput, BFormTextarea, BAlert, vSelect,
  },
  props: {
    order: { type: Object, required: true },
    trackingDetails: { type: Array, default: () => [] },
  },
  data() {
    return {
      show: false,
      loading: false,
      templateKey: 'absent',
      templateOptions: Object.entries(TEMPLATES).map(([value, t]) => ({ value, label: t.label })),
      vars: {},
      subject: '',
      body: '',
      isDirty: false,
    }
  },
  computed: {
    ...mapGetters('admin', ['me']),
    canSend() {
      return !!(this.vars.email && this.subject && this.body && !/{{\s*\w+\s*}}/.test(this.subject + this.body))
    },
  },
  watch: {
    templateKey(newKey, oldKey) {
      // eslint-disable-next-line no-alert
      if (this.isDirty && !window.confirm('Changer de modèle va écraser tes modifications. Continuer ?')) {
        this.$nextTick(() => { this.templateKey = oldKey })
        return
      }
      this.fill()
    },
  },
  methods: {
    open() {
      this.prefill()
      this.fill()
      this.show = true
    },
    prefill() {
      const o = this.order
      const meta = key => (o.meta_data || []).find(m => m.key === key)?.value
      const s = o.shipping || {}

      const adresse = [
        `${s.first_name || ''} ${s.last_name || ''}`.trim(),
        s.company,
        s.address_1,
        s.address_2,
        `${s.postcode || ''} ${s.city || ''}`.trim(),
        s.country,
        meta('_shipping_etage') ? `Étage : ${meta('_shipping_etage')}` : null,
        meta('_shipping_ascenseur') ? `Ascenseur : ${meta('_shipping_ascenseur')}` : null,
      ].filter(Boolean).join('\n')

      const signatureName = this.me?.firstName ? `${this.me.firstName}\n` : ''

      this.vars = {
        email: o.billing?.email || s.email || '',
        prenom: s.first_name || o.billing?.first_name || '',
        n_commande: String(o.number || ''),
        situation: 'vous étiez absent(e) lors de son passage',
        adresse,
        date_limite: this.$moment().add(DEFAULT_DELAY_DAYS, 'days').locale('fr').format('dddd D MMMM YYYY'),
        lien_tracking: this.getTrackingLink(),
        signature: `${signatureName}Le service client Kipli`,
      }
    },
    getTrackingLink() {
      // Uniquement les colis remontés par Shippingbo (PMO)
      const packages = (this.trackingDetails || []).filter(p => p && p.tracking_url)
      const dsv = packages.filter(p => String(p.carrier_name || '').toLowerCase().includes('dsv'))
      const links = [...new Set((dsv.length ? dsv : packages).map(p => p.tracking_url))]
      return links.join('\n')
    },
    interpolate(str) {
      return str.replace(/{{\s*(\w+)\s*}}/g, (m, key) => {
        const v = this.vars[key]
        return v !== undefined && v !== null && v !== '' ? v : m
      })
    },
    fill() {
      const tpl = TEMPLATES[this.templateKey]
      let { body } = tpl
      // Pas de tracking Shippingbo → on retire la ligne "Suivi du colis"
      if (!this.vars.lien_tracking) body = body.replace(/\n\nSuivi du colis : {{lien_tracking}}/, '')
      this.subject = this.interpolate(tpl.subject)
      this.body = this.interpolate(body)
      this.isDirty = false
    },
    toHtml(text) {
      const esc = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const inline = s => esc(s)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1">$1</a>')

      return (text || '').split(/\n{2,}/).map(block => {
        const out = []
        let para = []
        let list = []
        const flushPara = () => { if (para.length) out.push(`<p>${para.join('<br>')}</p>`); para = [] }
        const flushList = () => { if (list.length) out.push(`<ul>${list.map(li => `<li>${li}</li>`).join('')}</ul>`); list = [] }

        block.split('\n').forEach(line => {
          const bullet = line.match(/^\s*[•\-*]\s+(.*)$/)
          if (bullet) { flushPara(); list.push(inline(bullet[1])) } else { flushList(); para.push(inline(line)) }
        })
        flushPara()
        flushList()
        return out.join('')
      }).join('')
    },
    async send() {
      if (!this.canSend) return
      this.loading = true
      try {
        // 👉 À brancher sur ta route back qui pousse vers Klaviyo
        await this.$http.post(`/klaviyo/${this.$route.params.id}/dsv-email`, {
          template: this.templateKey,
          email: this.vars.email,
          orderNumber: this.order.number,
          subject: this.subject,
          body: this.body,
          body_html: this.toHtml(this.body),
        })

        await this.$http.put(`/admin/order/${this.$route.params.id}/addComment`, {
          text: `Mail DSV envoyé (${TEMPLATES[this.templateKey].label}) à ${this.vars.email}`,
        })

        this.$toast({
          component: ToastificationContentVue,
          props: { title: 'Mail envoyé à Klaviyo.', icon: 'MailIcon', variant: 'success' },
        })
        this.show = false
        this.$emit('sent')
      } catch (err) {
        console.log(err)
        this.$toast({
          component: ToastificationContentVue,
          props: { title: "Erreur lors de l'envoi du mail.", icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
